import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

import { WIX_ADS_ELEMENT_ID } from '../../../utils/constants';

// A top-anchored pinned bar reports top 0; the tolerance is for sub-pixel
// layout only.
const BANNER_TOP_TOLERANCE_PX = 2;

const isPinned = (element: Element) =>
  ['sticky', 'fixed'].includes(getComputedStyle(element).position);

const isVisible = (element: Element) => {
  const style = getComputedStyle(element);

  return (
    style.display !== 'none' &&
    style.visibility !== 'hidden' &&
    style.visibility !== 'collapse' &&
    Number(style.opacity || 1) > 0.01
  );
};

// Harmony pins the header by wrapping it in a fixed `*-pinned-layer`, leaving
// the header element itself relative, so pinning can sit on any ancestor.
const getPinnedAncestor = (element: Element): Element | null => {
  let current: Element | null = element;

  while (current && current !== document.body) {
    if (isPinned(current)) {
      return current;
    }

    current = current.parentElement;
  }

  return null;
};

// Classic pins #WIX_ADS itself; Harmony keeps that wrapper zero-height and
// absolute, and pins the banner link inside it.
const getPinnedBannerElement = (): Element | null => {
  const banner = document.getElementById(WIX_ADS_ELEMENT_ID);

  if (!banner || !isVisible(banner)) {
    return null;
  }

  // Only the bar pinned to the top of the viewport sits between the header and
  // the navigation. The wrapper can hold other pinned nodes, and the banner
  // itself is bottom-anchored on some breakpoints; reserving either would push
  // the navigation down the page for chrome that is not above it.
  const pinned = [banner, ...banner.querySelectorAll('*')].find(
    (element) =>
      isPinned(element) &&
      isVisible(element) &&
      element.getBoundingClientRect().top <= BANNER_TOP_TOLERANCE_PX,
  );

  return pinned || null;
};

// Classic animates the header itself; Studio animates its first child.
export const getSiteHeaderElements = (): Element[] => {
  const header = document.querySelector('header');
  const child = header?.firstElementChild;
  // A Classic header's first child can be a transparent background layer.
  // Only Studio's semantic header section represents the animated content.
  const section = child?.classList.contains('wixui-header') ? child : null;

  return header ? [header, ...(section ? [section] : [])] : [];
};

export const isSiteHeaderAnimating = () =>
  getSiteHeaderElements().some((element) =>
    element
      .getAnimations?.()
      .some((animation) => animation.playState === 'running'),
  );

export function getNavigationTopOffset(): number {
  if (typeof window === 'undefined') {
    return 0;
  }

  const pinnedBanner = getPinnedBannerElement();
  // Measured, never assumed: a premium site renders no banner at all, and a
  // guessed height would hold the navigation below a bar that is not there.
  const bannerBottom = pinnedBanner
    ? Math.max(0, pinnedBanner.getBoundingClientRect().bottom)
    : 0;
  const elements = getSiteHeaderElements();
  const pinnedLayer = elements
    .map(getPinnedAncestor)
    .find((element): element is Element => element !== null);

  if (
    elements.length === 0 ||
    !pinnedLayer ||
    !isVisible(pinnedLayer) ||
    !elements.every(isVisible)
  ) {
    return bannerBottom;
  }

  const outerRect = elements[0].getBoundingClientRect();
  const contentRect = elements[1]?.getBoundingClientRect();
  const headerRect = outerRect.height ? outerRect : contentRect || outerRect;
  // The child may translate while the sticky wrapper stays in place. Ignore
  // zero-sized layout wrappers, but otherwise use their visible intersection.
  const bottom = contentRect?.height
    ? Math.min(headerRect.bottom, contentRect.bottom)
    : headerRect.bottom;
  const top = contentRect?.height
    ? Math.max(headerRect.top, contentRect.top)
    : headerRect.top;

  const horizontallyOffscreen = [headerRect, contentRect].some(
    (rect) =>
      rect &&
      rect.width > 0 &&
      (rect.right <= 0 || rect.left >= window.innerWidth),
  );

  if (
    bottom <= top ||
    top >= window.innerHeight ||
    // A header taller than the viewport, or a full-screen mobile menu grown
    // out of one, would push the sticky navigation past the bottom edge, where
    // no amount of scrolling brings it back.
    bottom >= window.innerHeight ||
    horizontallyOffscreen
  ) {
    return bannerBottom;
  }

  return Math.max(bannerBottom, bottom);
}

// The navigation can mount already scrolled — a deep link to a section, or the
// browser restoring scroll on reload — and a post-paint measurement then shows
// it under the header for a frame. Measuring in the layout phase avoids that;
// the server has no layout phase, and the state still starts at 0 on both
// sides, so hydration matches.
const useMeasurementEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

export function useNavigationTopOffset(disabled: boolean) {
  const measureTopOffset = useCallback(
    () => (disabled ? 0 : getNavigationTopOffset()),
    [disabled],
  );
  const [navigationTopOffset, setNavigationTopOffset] = useState(0);

  useMeasurementEffect(() => {
    if (disabled) {
      setNavigationTopOffset(0);

      return;
    }

    let frame: number | undefined;
    let observed: Element[] = [];
    let animationDeadline = 0;
    const resizeObserver = new ResizeObserver(scheduleMeasure);
    const attributeObserver = new MutationObserver(scheduleMeasure);
    const animationEvents = [
      'transitionrun',
      'transitionend',
      'transitioncancel',
      'animationstart',
      'animationend',
      'animationcancel',
    ];

    function scheduleMeasure() {
      if (frame === undefined) {
        frame = requestAnimationFrame(update);
      }
    }

    function update() {
      frame = undefined;
      const headerElements = getSiteHeaderElements();
      const pinnedLayer = headerElements
        .map(getPinnedAncestor)
        .find(
          (element): element is Element =>
            element !== null && !headerElements.includes(element),
        );
      const banner = document.getElementById(WIX_ADS_ELEMENT_ID);
      const pinnedBanner = getPinnedBannerElement();
      const elements = [
        ...headerElements,
        ...(pinnedLayer ? [pinnedLayer] : []),
        ...(banner ? [banner] : []),
        ...(pinnedBanner && pinnedBanner !== banner ? [pinnedBanner] : []),
      ];

      if (
        elements.some((element, i) => element !== observed[i]) ||
        elements.length !== observed.length
      ) {
        resizeObserver.disconnect();
        attributeObserver.disconnect();
        observed.forEach((element) =>
          animationEvents.forEach((event) =>
            element.removeEventListener(event, onTransition),
          ),
        );
        observed = elements;
        observed.forEach((element) => {
          resizeObserver.observe(element);
          attributeObserver.observe(element, {
            attributes: true,
            attributeFilter: ['class', 'style', 'hidden'],
          });
          animationEvents.forEach((event) =>
            element.addEventListener(event, onTransition),
          );
        });
      }

      setNavigationTopOffset(measureTopOffset());
      if (performance.now() < animationDeadline && isSiteHeaderAnimating()) {
        scheduleMeasure();
      }
    }

    function onTransition() {
      // Only animate while needed; never keep a permanent scroll polling loop.
      animationDeadline = performance.now() + 5000;
      scheduleMeasure();
    }

    const structureObserver = new MutationObserver(scheduleMeasure);

    structureObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
    window.addEventListener('scroll', scheduleMeasure, { passive: true });
    window.addEventListener('resize', scheduleMeasure);
    update();

    return () => {
      if (frame !== undefined) {
        cancelAnimationFrame(frame);
      }
      resizeObserver.disconnect();
      attributeObserver.disconnect();
      structureObserver.disconnect();
      observed.forEach((element) =>
        animationEvents.forEach((event) =>
          element.removeEventListener(event, onTransition),
        ),
      );
      window.removeEventListener('scroll', scheduleMeasure);
      window.removeEventListener('resize', scheduleMeasure);
    };
  }, [disabled, measureTopOffset]);

  return { navigationTopOffset, measureTopOffset };
}
